import Button from "components/buttons/Button";
import { Illustration500, Illustrations404 } from "components/svgs/Illustrations";
import PageFooter from "layout/PageFooter";
import { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/routes.const";

type ErrorCode = 401 | 404 | 500;

interface Props {
  title: string;
  description: string;
  errorCode: ErrorCode;
}

const ILLUSTRATION_BY_CODE: Partial<
  Record<ErrorCode, { Illustration: ComponentType; wrapperClassName: string }>
> = {
  404: {
    Illustration: Illustrations404,
    wrapperClassName: "order-first mb-16 hidden w-80 sm:block lg:order-last lg:w-full lg:max-w-md",
  },
  500: {
    Illustration: Illustration500,
    wrapperClassName: "order-first mb-16 hidden w-96 sm:block lg:order-last lg:w-full lg:max-w-xl",
  },
};

const ErrorLayoutPage = ({ title, errorCode, description }: Props) => {
  const { t } = useTranslation("error");
  const navigate = useNavigate();
  const illustration = ILLUSTRATION_BY_CODE[errorCode];

  return (
    <div className="flex min-h-full flex-col pt-16">
      <main className="mx-auto flex w-full grow flex-col px-4 sm:justify-center sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center gap-4 py-16 lg:flex-row">
          <div className="text-center">
            <p className="text-base font-semibold uppercase tracking-wide text-ribbon-500 dark:text-ribbon-300">
              {t("page.error-code", { errorCode })}
            </p>
            <h1 className="text-color-primary mx-auto mt-2 max-w-2xl text-4xl/normal font-extrabold tracking-tight sm:text-5xl/normal">
              {title}
            </h1>
            <p className="text-color-secondary mt-2 text-base">{description}</p>
            <div className="mt-6">
              <Button
                onClick={() => navigate(ROUTES.HOME, { replace: true })}
                label={t("page.goBackHome")}
                className="inline-flex items-center"
              />
            </div>
          </div>
          {illustration && (
            <div className={illustration.wrapperClassName}>
              <illustration.Illustration />
            </div>
          )}
        </div>
      </main>
      <PageFooter />
    </div>
  );
};

export default ErrorLayoutPage;
